import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import * as roomService from '@/services/roomService'
import * as priceOverrideService from '@/services/priceOverrideService'
import type { RoomDetail } from '@/types/room'
import type { PriceOverride } from '@/types/priceOverride'
import type { RoomType } from '@/types/schedule'
import { PriceCalendar } from './price-calendar'
import { PriceOverrideModal } from './price-override-modal'

type FilterMode = 'room' | 'type'

const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  standard: 'Standard',
  vip: 'VIP',
  supervip: 'Super VIP',
}

const DEFAULT_PRICES = {
  hourlyRate: 0,
  dailyRate: 0,
  overnightRate: 0,
  extraHourRate: 0,
  combo3hRate: 0,
  combo6h1hRate: 0,
  combo6h1hDiscount: 0,
}

const DAY_OPTIONS = [
  { label: 'T2', day: 1 },
  { label: 'T3', day: 2 },
  { label: 'T4', day: 3 },
  { label: 'T5', day: 4 },
  { label: 'T6', day: 5 },
  { label: 'T7', day: 6 },
  { label: 'CN', day: 0 },
] as const

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function getDatesForDays(year: number, month: number, days: number[]): string[] {
  const result: string[] = []
  const daysInMonth = new Date(year, month, 0).getDate()
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month - 1, d)
    if (days.includes(date.getDay())) {
      result.push(`${year}-${pad2(month)}-${pad2(d)}`)
    }
  }
  return result
}

export function PriceOverridePage() {
  // Current month view
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  // Filter mode & selection
  const [filterMode, setFilterMode] = useState<FilterMode>('room')
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(null)

  // Data
  const [rooms, setRooms] = useState<RoomDetail[]>([])
  const [overrides, setOverrides] = useState<PriceOverride[]>([])

  // Calendar selection
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>([])

  // Modal
  const [modalOpen, setModalOpen] = useState(false)

  // Derived: default prices from selected room
  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) ?? null
  const defaultPrices = selectedRoom
    ? {
        hourlyRate: selectedRoom.hourlyRate,
        dailyRate: selectedRoom.dailyRate,
        overnightRate: selectedRoom.overnightRate,
        extraHourRate: selectedRoom.extraHourRate,
        combo3hRate: selectedRoom.combo3hRate,
        combo6h1hRate: selectedRoom.combo6h1hRate,
        combo6h1hDiscount: selectedRoom.combo6h1hDiscount,
      }
    : DEFAULT_PRICES

  // Fetch rooms on mount
  useEffect(() => {
    roomService.getAll().then(setRooms).catch(() => {
      toast.error('Không thể tải danh sách phòng')
    })
  }, [])

  // Auto-select first room when rooms load
  useEffect(() => {
    if (rooms.length > 0 && !selectedRoomId && filterMode === 'room') {
      const timer = setTimeout(() => setSelectedRoomId(rooms[0].id), 0)
      return () => clearTimeout(timer)
    }
  }, [rooms, selectedRoomId, filterMode])

  // Fetch overrides when filter or month changes
  const fetchOverrides = useCallback(async () => {
    const monthStr = `${year}-${pad2(month)}`
    try {
      if (filterMode === 'room' && selectedRoomId) {
        const data = await priceOverrideService.getByMonth({
          roomId: selectedRoomId,
          month: monthStr,
        })
        setOverrides(data)
      } else if (filterMode === 'type' && selectedRoomType) {
        const data = await priceOverrideService.getByMonth({
          roomType: selectedRoomType,
          month: monthStr,
        })
        setOverrides(data)
      } else {
        setOverrides([])
      }
    } catch {
      toast.error('Không thể tải dữ liệu giá')
    }
  }, [year, month, filterMode, selectedRoomId, selectedRoomType])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const monthStr = `${year}-${pad2(month)}`
      try {
        let data: typeof overrides = []
        if (filterMode === 'room' && selectedRoomId) {
          data = await priceOverrideService.getByMonth({ roomId: selectedRoomId, month: monthStr })
        } else if (filterMode === 'type' && selectedRoomType) {
          data = await priceOverrideService.getByMonth({ roomType: selectedRoomType, month: monthStr })
        }
        if (!cancelled) setOverrides(data)
      } catch {
        if (!cancelled) toast.error('Không thể tải dữ liệu giá')
      }
    }
    run()
    return () => { cancelled = true }
  }, [year, month, filterMode, selectedRoomId, selectedRoomType])

  // Month navigation
  const prevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1)
      setMonth(12)
    } else {
      setMonth((m) => m - 1)
    }
    setSelectedDates([])
    setSelectedDaysOfWeek([])
  }

  const nextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1)
      setMonth(1)
    } else {
      setMonth((m) => m + 1)
    }
    setSelectedDates([])
    setSelectedDaysOfWeek([])
  }

  // Calendar interaction
  const handleSelectDate = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    )
  }

  const handleSelectRange = (dates: string[]) => {
    setSelectedDates((prev) => [...new Set([...prev, ...dates])])
  }

  // Open modal when dates are selected
  const openModal = () => {
    if (selectedDates.length === 0) return
    setModalOpen(true)
  }

  // Find existing overrides for selected dates
  const existingOverrides = overrides.filter((o) => selectedDates.includes(o.date))

  const target =
    filterMode === 'room'
      ? { roomId: selectedRoomId ?? undefined }
      : { roomType: selectedRoomType ?? undefined }

  const hasSelection = filterMode === 'room' ? !!selectedRoomId : !!selectedRoomType

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">Giá theo ngày</h1>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Filter mode toggle */}
        <div className="flex rounded-lg border overflow-hidden">
          <button
            type="button"
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filterMode === 'room'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background hover:bg-accent'
            }`}
            onClick={() => {
              setFilterMode('room')
              setSelectedDates([])
              setSelectedDaysOfWeek([])
            }}
          >
            Theo phòng
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              filterMode === 'type'
                ? 'bg-primary text-primary-foreground'
                : 'bg-background hover:bg-accent'
            }`}
            onClick={() => {
              setFilterMode('type')
              setSelectedRoomType('standard')
              setSelectedDates([])
              setSelectedDaysOfWeek([])
            }}
          >
            Theo loại
          </button>
        </div>

        {/* Room / Type selector */}
        {filterMode === 'room' ? (
          <Select
            value={selectedRoomId ?? ''}
            onValueChange={(v) => {
              setSelectedRoomId(v)
              setSelectedDates([])
              setSelectedDaysOfWeek([])
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Chọn phòng" />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select
            value={selectedRoomType ?? ''}
            onValueChange={(v) => {
              setSelectedRoomType(v as RoomType)
              setSelectedDates([])
              setSelectedDaysOfWeek([])
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Chọn loại phòng" />
            </SelectTrigger>
            <SelectContent>
              {(Object.entries(ROOM_TYPE_LABELS) as [RoomType, string][]).map(
                ([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        )}

        {/* Quick day-of-week selection */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <CalendarDays className="w-4 h-4 mr-1.5" />
              Chọn theo thứ
              {selectedDaysOfWeek.length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary text-primary-foreground text-xs px-1.5">
                  {selectedDaysOfWeek.length}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Chọn thứ</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {DAY_OPTIONS.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.day}
                checked={selectedDaysOfWeek.includes(opt.day)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    const newDays = [...selectedDaysOfWeek, opt.day]
                    setSelectedDaysOfWeek(newDays)
                    const datesToAdd = getDatesForDays(year, month, [opt.day])
                    setSelectedDates((prev) => [...new Set([...prev, ...datesToAdd])])
                  } else {
                    setSelectedDaysOfWeek((prev) => prev.filter((d) => d !== opt.day))
                    const datesToRemove = new Set(getDatesForDays(year, month, [opt.day]))
                    setSelectedDates((prev) => prev.filter((d) => !datesToRemove.has(d)))
                  }
                }}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Month navigation */}
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="outline" size="icon" onClick={prevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-medium min-w-[120px] text-center">
            Tháng {month}/{year}
          </span>
          <Button variant="outline" size="icon" onClick={nextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Calendar */}
      {hasSelection ? (
        <PriceCalendar
          year={year}
          month={month}
          overrides={overrides}
          defaultPrices={defaultPrices}
          selectedDates={selectedDates}
          onSelectDate={handleSelectDate}
          onSelectRange={handleSelectRange}
        />
      ) : (
        <div className="text-sm text-muted-foreground py-8 text-center">
          Vui lòng chọn phòng hoặc loại phòng để xem lịch giá
        </div>
      )}

      {/* Action bar */}
      {selectedDates.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
          <span className="text-sm">
            Đã chọn <strong>{selectedDates.length}</strong> ngày
          </span>
          <Button size="sm" onClick={openModal}>
            Chỉnh giá
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSelectedDates([])}
          >
            Bỏ chọn
          </Button>
        </div>
      )}

      {/* Modal */}
      <PriceOverrideModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dates={selectedDates}
        existingOverrides={existingOverrides}
        defaultPrices={defaultPrices}
        target={target}
        onSave={() => {
          fetchOverrides()
          setSelectedDates([])
        }}
      />
    </div>
  )
}
